import { theme } from '@acorns/grove'
import { AcornsIcon } from '@acorns/icons'
import { Drawer } from '@acorns/web-components'
import styled, { css } from 'styled-components'

export const StyledDrawer = styled(Drawer)`
  max-height: 100%;
  max-width: 100%;
  border-radius: 1rem;
  padding: 30px 30px 0;
  text-align: center;
  overflow-x: hidden;

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    max-height: 90vh;

    > div {
      max-height: 90vh;
    }
  }

  .bounce-in-bottom {
    animation: bounce-in-bottom 0.45s cubic-bezier(0.23, 1.25, 0.46, 1) both;
  }
`

export const StyledSeparator = styled.div`
  height: 1px;
  background-color: ${theme.colors.primaryIvory};
  width: calc(100% + 70px);
  margin-bottom: 0;
  margin-left: -35px;
`

export const CompareTitle = styled.h2`
  font-weight: 600;
  font-size: 26px;
  line-height: 36px;
  color: ${theme.colors.primarySlate};
  margin-top: 20px;
  margin-bottom: 20px;
  text-align: center;
  flex-shrink: 0;
`

export const DrawerContent = styled.div`
  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    display: flex;
    flex-direction: column;
    max-height: calc(90vh - 130px);
    overflow: hidden;
  }
`

export const TableWrapper = styled.div`
  max-height: 60vh;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  justify-content: center;
  scrollbar-width: none;
  -ms-overflow-style: none;
  position: relative;

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    max-height: none;
    flex: 1;
    min-height: 0;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

export const FooterContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  padding: 20px 30px;
  display: flex;
  gap: 20px;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: ${theme.colors.primaryWhite};

  @media (min-width: ${theme.dimensions.breakpointsSmall + 1}px) {
    position: unset;
    padding-top: 30px;
    padding-bottom: 30px;
    border-bottom-left-radius: ${theme.borderRadius.large};
    border-bottom-right-radius: ${theme.borderRadius.large};
  }
`

export const DoneButton = styled.button`
  background: ${theme.colors.primaryGreen};
  color: ${theme.colors.primaryWhite};
  font-weight: 600;
  font-size: 16px;
  border: none;
  border-radius: 100px;
  padding: 12px 48px;
  cursor: pointer;
  transition: background 0.2s;
  width: 340px;
`

export const CompareTable = styled.table`
  table-layout: fixed;
  border-collapse: collapse;
  margin: 0 180px 32px 180px;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    margin: 0 0 180px 0;
    width: 100%;
  }
`

export const HeaderLeft = styled.th`
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  padding-left: 10px;
  color: ${theme.colors.primaryStoneLight};
  border: none;
  position: sticky;
  top: 0;
  z-index: 3;
  background: ${theme.colors.primaryWhite};

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    font-size: 12px;
    top: -1px;
  }
`

export const Header = styled.th<{ isGold: boolean }>`
  position: sticky;
  top: 0;
  z-index: 3;
  width: 50px;
  ${(props) => props.isGold && 'height: 45px;'}
  padding: 0;
  font-weight: 500;
  font-size: 12px;
  text-align: center;
  background: ${theme.colors.primaryWhite};
  color: ${(props) =>
    props.isGold ? theme.colors.primaryWhite : theme.colors.primaryStone};

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    width: 45px;
  }

  ${(props) =>
    props.isGold &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: #dca613;
        border-radius: 15px 15px 0 0;
        z-index: -1;
      }

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: ${theme.colors.primaryWhite};
        z-index: -2;
      }
    `}
`

export const Section = styled.td`
  display: flex;
  align-items: flex-end;
  gap: 5px;
  padding: 10px 0 0 10px;
  font-size: 18px;
  font-weight: 600;
  border: none;
  letter-spacing: 0.5px;
  text-align: left;
  margin-left: -10px;

  &:first-child {
    padding-top: 20px;
  }
`

export const Feature = styled.td`
  overflow: hidden;
  vertical-align: middle;
  padding-left: 10px;
  background: ${theme.colors.primaryWhite};
  font-size: 16px;
  width: 220px;
  padding-right: 5px;
  text-align: left;
  font-weight: 600;
  color: ${theme.colors.primarySlate};
`

export const Row = styled.tr<{ isBundleHeader?: boolean; isLast?: boolean }>`
  & > td,
  & > th {
    height: ${({ isBundleHeader }) => (isBundleHeader ? '50px' : '70px')};
    overflow: hidden;
    vertical-align: middle;
    padding-top: 15px;
  }

  &:first-child > td,
  &:first-child > th {
    height: 40px;
  }

  /* stylelint-disable-next-line selector-type-no-unknown */
  &:has(${Section}) {
    & > td,
    & > th {
      padding-top: 0;
    }
  }
`

export const DividerCell = styled.td`
  padding: 0;
  height: 1px;
`

export const DividerLine = styled.div`
  height: 1px;
  background-color: ${theme.colors.primarySlate}0d;
  margin: 0 10px;
`

export const FeatureTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  margin: 0;
`

export const FeatureDesc = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.primaryStone};
  margin-top: 0;
  margin-bottom: 15px;
`

export const Cell = styled.td<{ isGold?: boolean; isBundleHeader?: boolean }>`
  background: ${({ isGold }) =>
    isGold ? '#fbf6e7' : theme.colors.primaryWhite};
  text-align: center;
  position: relative;
  z-index: 1;
  padding: ${({ isBundleHeader }) => (isBundleHeader ? '15px 0' : 'inherit')};
  height: ${({ isBundleHeader }) => (isBundleHeader ? '50px' : 'inherit')};

  ${({ isGold }) =>
    isGold &&
    css`
      tbody tr:last-child & {
        border-bottom-left-radius: 15px;
        border-bottom-right-radius: 15px;
      }
    `}
`

export const Check = styled(AcornsIcon)<{ gold?: boolean }>`
  width: 14px;
  height: 11px;
  display: inline-block;
  path {
    stroke: ${({ gold }) =>
      gold ? '#DCA613' : theme.colors.primaryStoneLight};
  }
`

export const Dot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 10px;
  color: ${theme.colors.primaryStoneLight};
`

export const MatchValue = styled.span<{ gold?: boolean }>`
  font-weight: 600;
  font-size: 14px;
  line-height: 26px;
  display: inline-flex;
  align-items: center;
  text-align: center;
  color: ${({ gold }) =>
    gold ? theme.colors.naturalMushroom : theme.colors.primaryStoneLight};

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    max-width: 40px;
  }
`
